import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { Observable, Subscription } from "rxjs";
import { COMMAND_NAME_VALID_REGEXP, RBE_AUTOMATION_NAME_VALID_REGEXP } from "src/app/conf/commands";
import { Command } from "src/app/model/command";
import { ConnectedAutomations } from "src/app/model/device";
import { StandardNewValidator, StandardPageLayout } from "src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib";
import { AppState } from "src/app/store/app.reducer";
import * as DeviceActions from 'src/app/store/device/device.actions';
import { selectCommandsEdited } from "src/app/store/device/device.selector";

interface EditPayload {
	commands: Command[];
}

@Component({
	selector: 'app-command-alias-automation-dialog',
	templateUrl: './command-alias-automation-dialog.component.html',
})

export class CommandAliasAutomationDialogComponent implements OnInit, OnDestroy {
	commandsEdited$: Observable<boolean | undefined>;
	commandsEditedSub: Subscription | undefined;

	aliasForm: FormGroup;

	commands: Command[] = [];
	connectedAutomations: ConnectedAutomations;

	screenWidth: number;

	constructor(
		private formBuilder: FormBuilder,
		private store: Store<AppState>,
		public ref: DynamicDialogRef,
		public config: DynamicDialogConfig
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.commandsEdited$ = this.store.select(selectCommandsEdited);
	}

	ngOnInit(): void {
		if (this.config.data !== undefined && this.config.data.connectedAutomations !== undefined) {
			this.connectedAutomations = this.config.data.connectedAutomations;
		}
		if (this.config.data !== undefined && this.config.data.commands !== undefined) {
			this.commands = this.config.data.commands;
			this.aliasForm = this.formBuilder.group(this.buildForm(this.commands));
		}
		this.store.dispatch(DeviceActions.getCommands({}));
		this.commandsEditedSub = this.commandsEdited$.subscribe(succ => {
			if (succ) {
				this.store.dispatch(DeviceActions.getCommands({}));
				this.ref.close();
			}
		});
		StandardNewValidator.setFirstCompile(true);
		this.onResize();
	}

	buildForm(commands: Command[]) {
		const res = {};
		commands.forEach((command) => {
			res[command.Name] = [command.Description, [Validators.required, Validators.pattern(COMMAND_NAME_VALID_REGEXP)]];
		});
		res['NameAutomation'] = [this.connectedAutomations.Name, [Validators.required, Validators.pattern(RBE_AUTOMATION_NAME_VALID_REGEXP)]]
		return res;
	}

	restoreDefault(name: string) {
		this.aliasForm.controls[name].setValue(this.commandDefaultValue(name));
	}

	commandDefaultValue(name: string): string {
		return this.commands.find(cmd => cmd.Name === name).Default;
	}

	ngOnDestroy(): void {
		this.commandsEditedSub?.unsubscribe();
	}

	get f(): any {
		return this.aliasForm.controls;
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.aliasForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.aliasForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save(): void {
		const payload: EditPayload = { commands: [] };
		for (const command of this.commands) {
			const cmd: Command = { ...command, Description: this.aliasForm.controls[command.Name].value };
			payload.commands.push(cmd);
		}
		if (this.connectedAutomations.Name !== this.aliasForm.controls['NameAutomation'].value) {
			const editName = { Id: this.connectedAutomations.Id, Name: this.aliasForm.controls['NameAutomation'].value };
			this.store.dispatch(DeviceActions.editNameAutomationConnected({ automation: editName }));
		}
		this.store.dispatch(DeviceActions.editCommands(payload));
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	newShowErrors(type: CommandAliasAutomationDialogComponent['f'], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: CommandAliasAutomationDialogComponent['f']): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}
}